import {
  Button,
  DataTable,
  SectionCard,
  Skeleton,
  StatusBadge,
  type DataTableColumn,
} from '@/components/ui';
import { useExchangeRateHistoryQuery } from '../hooks/useExchangeRatesQuery';
import { formatAdjustment, formatDateTime, formatRate } from '../lib/format';
import type { ExchangeRateChangeSource, ExchangeRateHistoryItem } from '../types';

const SOURCE_LABEL: Record<ExchangeRateChangeSource, string> = {
  MANUAL: '수동',
  AUTO: '자동',
  TEST: '테스트',
};

const SOURCE_VARIANT: Record<ExchangeRateChangeSource, 'info' | 'success' | 'neutral'> = {
  MANUAL: 'info',
  AUTO: 'success',
  TEST: 'neutral',
};

/** 환율 변경 이력 리스트. 가격 영향 추적·감사용 — 최신순으로 표시한다(정렬은 BE 책임). */
export const ExchangeRateHistoryCard = () => {
  const query = useExchangeRateHistoryQuery();

  const columns: DataTableColumn<ExchangeRateHistoryItem>[] = [
    {
      key: 'changedAt',
      header: '변경 일시',
      width: 150,
      cell: (row) => (
        <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatDateTime(row.changedAt)}
        </span>
      ),
    },
    {
      key: 'currencyCode',
      header: '통화',
      width: 72,
      cell: (row) => (
        <span className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
          {row.currencyCode}
        </span>
      ),
    },
    {
      key: 'change',
      header: '환율 변경',
      cell: (row) => (
        <span className="flex items-center gap-2 text-[13px] tabular-nums">
          <span style={{ color: 'var(--content-muted)' }}>{formatRate(row.oldKrwPerUnit)}</span>
          <span style={{ color: 'var(--content-muted)' }}>→</span>
          <span style={{ color: 'var(--content-strong)', fontWeight: 600 }}>
            {formatRate(row.newKrwPerUnit)}
          </span>
          <span style={{ color: 'var(--content-muted)' }}>KRW</span>
        </span>
      ),
    },
    {
      key: 'baseAdjustment',
      header: '기준/조정',
      width: 150,
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.baseRate !== null ? formatRate(row.baseRate) : '—'} ({formatAdjustment(row.adjustment)})
        </span>
      ),
    },
    {
      key: 'source',
      header: '출처',
      width: 80,
      align: 'center',
      cell: (row) => (
        <StatusBadge label={SOURCE_LABEL[row.source]} variant={SOURCE_VARIANT[row.source]} size="sm" />
      ),
    },
    {
      key: 'changedBy',
      header: '변경자',
      width: 130,
      cell: (row) => (
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          {row.changedBy ?? '시스템'}
        </span>
      ),
    },
  ];

  return (
    <SectionCard title="변경 이력" description="환율 변경 내역을 최신순으로 보여줍니다.">
      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[40px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-2 rounded-lg py-8 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>변경 이력을 불러오지 못했습니다.</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            다시 시도
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={query.data ?? []}
          rowKey={(row) => row.id}
          emptyState="변경 이력이 없습니다."
          stickyHeader
          ariaLabel="환율 변경 이력"
        />
      )}
    </SectionCard>
  );
};
